import { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Theme,
  useMediaQuery,
} from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useTitle } from "utils/UseTitle";
import {
  WebForm,
  fetchWebForms,
  fetchWebFormsCount,
  deleteWebForm,
  updateWebForm,
} from "./WebFormsService";
import {
  handleErrorMessage,
  openSuccessNotification,
  openErrorNotification,
} from "helpers/methods";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { WebFormRow } from "./components/WebFormRow";
import WebFormHeader from "./components/WebFormHeader";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import { ChevronDownGlyph } from "./components/WebFormsIcons";
import {
  renderWebFormSkeletons,
  WebFormDeleteDialog,
  WebFormRenameDialog,
} from "./WebFormHelper";
import { CreateWebFormModal } from "./CreateWebFormModal";
import { aiBuilderPath, sessionOfDesign } from "./AiBuilder/AiWebFormHelper";
import { listAiSessions } from "./AiBuilder/AiWebFormService";
import { LiveRegion } from "components/live-region";
import { useFeatureState, useFeatures } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import webFormsStyles from "./WebForms.styles";
import webFormsTokens from "./WebForms.tokens";
import SharedEmptyState from "global/components/EmptyState/EmptyState";
import { tourAttr } from "tour";

const styles = webFormsStyles;
const tokens = webFormsTokens;

const PAGE_SIZE = 12;

/** Sits inside the list card, in place of the rows. */
const EmptyState = () => (
  <SharedEmptyState
    icon="template"
    title="No web forms yet"
    body="Turn a template into a shareable form and it will show up here."
  />
);

const WebFormsList = () => {
  useTitle(title.WEBFORMS);
  /**
   * The phone reference drops the `<h1>` from the page body and carries the
   * screen name in the app bar instead. The tablet reference keeps it on the
   * page, so the app bar is only fed below `sm`.
   */
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  useSetPageHeader(isPhone ? "Web Forms" : undefined);

  const webFormsState = useFeatureState(FeatureKeys.ENVELOPE_WEB_FORMS);
  const { loaded: featuresLoaded } = useFeatures();
  const webFormsAvailable =
    featuresLoaded && webFormsState.status === strings.FEATURE_STATUS_AVAILABLE;

  const [webForms, setWebForms] = useState<WebForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);
  const dataCountRef = useRef(0);
  const webFormsLengthRef = useRef(0);

  const [selectedForm, setSelectedForm] = useState<WebForm | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renameDialog, setRenameDialog] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);

  const loadWebForms = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      setIsLoading(true);
      const [formsResult, countResult] = await Promise.allSettled([
        fetchWebForms(pageNumber, PAGE_SIZE),
        fetchWebFormsCount(),
      ]);

      if (formsResult.status === "fulfilled") {
        setWebForms((prev) =>
          pageNumber === 1
            ? formsResult.value
            : [...prev, ...formsResult.value],
        );
      } else {
        handleErrorMessage(formsResult.reason);
      }

      if (countResult.status === "fulfilled") {
        setDataCount(countResult.value);
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [pageNumber]);

  useEffect(() => {
    dataCountRef.current = dataCount;
  }, [dataCount]);
  useEffect(() => {
    webFormsLengthRef.current = webForms.length;
  }, [webForms]);

  useEffect(() => {
    if (!webFormsAvailable) {
      // The list never loads while the plan blocks it, so clear the skeleton
      // rather than leaving the page shimmering behind the banner forever.
      if (featuresLoaded) setIsLoading(false);
      return;
    }
    loadWebForms();
  }, [pageNumber, loadWebForms, webFormsAvailable, featuresLoaded]);

  useEffect(() => {
    const currentObserver = observerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !isFetchingRef.current &&
          webFormsLengthRef.current < dataCountRef.current
        ) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 1.0 },
    );

    if (currentObserver) {
      observer.observe(currentObserver);
    }

    return () => {
      if (currentObserver) {
        observer.unobserve(currentObserver);
      }
    };
  }, []);

  const handleCreate = (data: any) => {
    if (!data.templateId) {
      openErrorNotification(
        "A template must be selected to create a web form.",
      );
      return;
    }
    const signerId =
      data.signers && data.signers.length > 0 ? data.signers[0] : 1;
    setIsModalOpen(false);
    history.push(
      `${viewpaths.webFormsViewPath}/builder/new?templateId=${data.templateId}&signerId=${signerId}&name=${encodeURIComponent(data.name)}`,
    );
  };

  /** The chat behind an AI form saved without its session id, found by its design. */
  const aiSessionOf = async (form: WebForm, designId?: string) => {
    try {
      const list = await listAiSessions({ templateId: form.templateId, signerId: form.signerId, limit: 50 });
      if (designId) {
        return sessionOfDesign(list.sessions, designId);
      }
      return list.sessions && list.sessions.length > 0 ? list.sessions[0].sessionId : undefined;
    } catch {
      return undefined;
    }
  };

  const handleUpdate = async (form: WebForm) => {
    // A form built with the AI designer has no Unlayer design: it reopens its chat, never the Unlayer
    // builder, whose save would overwrite the AI's page with an empty design.
    const isAi = Boolean(form.aiDesignId || (!form.unlayerJson && form.htmlKey));
    if (isAi) {
      let sessionId = form.aiSessionId;
      if (!sessionId) {
        setIsLoading(true);
        try {
          sessionId = await aiSessionOf(form, form.aiDesignId);
        } finally {
          setIsLoading(false);
        }
      }
      if (!sessionId) {
        openErrorNotification(strings.aiWebForm.chatNotFound);
        return;
      }
      history.push(
        aiBuilderPath({
          sessionId,
          templateId: form.templateId,
          signerId: form.signerId,
          name: form.name,
        }),
      );
      return;
    }
    history.push(`${viewpaths.webFormsViewPath}/builder/${form.id}`);
  };

  const handleDeleteClick = (form: WebForm) => {
    setSelectedForm(form);
    setDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialog(false);
    setSelectedForm(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedForm?.id) return;
    try {
      setIsDeleting(true);
      await deleteWebForm(selectedForm.id);
      openSuccessNotification("Web Form deleted successfully!");

      setWebForms((prev) => prev.filter((f) => f.id !== selectedForm?.id));
      setDataCount((prev) => prev - 1);

      setDeleteDialog(false);
      setTimeout(() => setSelectedForm(null), 300);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRenameClick = (form: WebForm) => {
    setSelectedForm(form);
    setRenameDialog(true);
  };

  const handleRenameConfirm = async (newName: string) => {
    if (!selectedForm?.id) return;
    const formId = selectedForm.id;
    try {
      setIsRenaming(true);
      const formData = new FormData();
      formData.append("name", newName);

      await updateWebForm(formId, formData);
      openSuccessNotification("Web Form renamed successfully!");
      setRenameDialog(false);
      setSelectedForm(null);

      setWebForms((prev) =>
        prev.map((f) => (f.id === formId ? { ...f, name: newName } : f)),
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsRenaming(false);
    }
  };

  /**
   * Loading, empty and result-count changes are silent DOM swaps for a screen
   * reader, so they are mirrored into a polite live region (4.1.3).
   */
  const statusMessage =
    isLoading && webForms.length === 0
      ? "Loading web forms"
      : webForms.length === 0
        ? "No web forms yet"
        : `${webForms.length} of ${dataCount} web forms shown${
            isLoading ? ", loading more" : ""
          }`;

  /**
   * Closes the card with the running tally. More pages arrive on scroll, so the
   * strip nudges towards them rather than offering a pager.
   *
   * Below `md` the reference moves the tally out of the card and onto the page
   * as a bare "Showing N of M" line — see `getPageFooter`.
   */
  const getFooter = () => {
    if (webForms.length === 0) return null;
    const hasMore = webForms.length < dataCount;

    return (
      <Box sx={styles.footer}>
        {isLoading ? (
          <>
            <CircularProgress
              size={tokens.table.footerSpinnerSize}
              thickness={tokens.table.footerSpinnerThickness}
              sx={styles.footerSpinner}
            />
            Loading more…
          </>
        ) : (
          <>
            {hasMore && (
              <>
                <ChevronDownGlyph
                  sx={{
                    width: tokens.table.footerIconSize,
                    height: tokens.table.footerIconSize,
                  }}
                />
                Scroll to load more ·
              </>
            )}
            <Box component="span">
              showing{" "}
              <Box component="span" sx={styles.footerValue}>
                {webForms.length}
              </Box>{" "}
              of{" "}
              <Box component="span" sx={styles.footerValue}>
                {dataCount}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  };

  /** Phone and tablet stand-in for the card footer, hidden from `md` up. */
  const getPageFooter = () => {
    if (webForms.length === 0) return null;

    return (
      <Box sx={styles.pageFooter} aria-hidden="true">
        {isLoading ? (
          <>
            <CircularProgress
              size={tokens.table.footerSpinnerSize}
              thickness={tokens.table.footerSpinnerThickness}
              sx={styles.footerSpinner}
            />
            Loading more…
          </>
        ) : (
          <Box component="span">
            Showing {webForms.length} of {dataCount}
          </Box>
        )}
      </Box>
    );
  };

  // The header carries its own bottom margin, as on Sign Requests, so the page
  // is a plain padded column rather than an evenly spaced Stack.
  const getPage = () => (
    <Box sx={styles.page}>
      {featuresLoaded &&
      webFormsState.status !== strings.FEATURE_STATUS_AVAILABLE ? (
        <FeatureGateBlock
          variant="full"
          featureName="Web forms"
          status={webFormsState.status as "not-in-plan" | "admin-disabled"}
          disabledMessage={(webFormsState as any).disabledMessage}
        />
      ) : (
        <>
          <WebFormHeader
            onCreateClick={() => setIsModalOpen(true)}
            createDisabled={!webFormsAvailable}
            totalCount={dataCount}
          />
          <Box sx={styles.tableCard} {...tourAttr("webforms.list")}>
            {/*
             * Visually a data table (column-header strip over grid rows), so the
             * header→cell relationships are restored with the ARIA table pattern
             * (1.3.1) without touching a style. Skeletons are hidden from AT; the
             * live region below covers the loading/count state (4.1.3).
             */}
            <Box
              role="table"
              aria-label="Web forms"
              aria-busy={isLoading || undefined}
            >
              <Box role="row" sx={styles.columnHeader}>
                <Box component="span" role="columnheader">
                  NAME
                </Box>
                <Box
                  component="span"
                  role="columnheader"
                  sx={styles.columnHeaderCell}
                >
                  TEMPLATE
                </Box>
                <Box
                  component="span"
                  role="columnheader"
                  sx={styles.columnHeaderCellRight}
                >
                  CREATED
                </Box>
                <Box component="span" role="columnheader">
                  <Box component="span" sx={styles.visuallyHidden}>
                    Actions
                  </Box>
                </Box>
              </Box>

              {isLoading && webForms.length === 0 ? (
                <Box aria-hidden="true" sx={styles.rowGroup}>
                  {renderWebFormSkeletons()}
                </Box>
              ) : (
                webForms.length > 0 && (
                  <Box role="rowgroup" sx={styles.rowGroup}>
                    {webForms.map((form) => (
                      <WebFormRow
                        key={form.id}
                        form={form}
                        onRenameClick={handleRenameClick}
                        onUpdateClick={handleUpdate}
                        onDeleteClick={handleDeleteClick}
                      />
                    ))}
                  </Box>
                )
              )}

              {isLoading && webForms.length > 0 && (
                <Box aria-hidden="true" sx={styles.rowGroup}>
                  {renderWebFormSkeletons()}
                </Box>
              )}
            </Box>

            {!isLoading && webForms.length === 0 && <EmptyState />}
            {getFooter()}
          </Box>
        </>
      )}

      {getPageFooter()}

      <LiveRegion id="webforms-status" message={statusMessage} />
      <div ref={observerRef} style={{ height: "20px", marginTop: "20px" }} />

      <WebFormDeleteDialog
        open={deleteDialog}
        formName={selectedForm?.name}
        isDeleting={isDeleting}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />

      <WebFormRenameDialog
        open={renameDialog}
        initialName={selectedForm?.name}
        isRenaming={isRenaming}
        onClose={() => {
          setRenameDialog(false);
          setSelectedForm(null);
        }}
        onConfirm={handleRenameConfirm}
      />

      <CreateWebFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onComplete={handleCreate}
      />
    </Box>
  );

  return <CenteredAuthenticatedLayout>{getPage()}</CenteredAuthenticatedLayout>;
};

export default WebFormsList;
